import React, { useState } from "react";
import Select from "react-select";
import { customSelect } from "@/javascript/components/utils/StyledSelect";
import CeMap from "@/javascript/manufacturers/shared/designations/CeMap";
import GranteeExemptionNotice from "@/javascript/manufacturers/shared/designations/notices/GranteeExemptionNotice";
import WhollyOwnedExemptionNotice from "@/javascript/manufacturers/shared/designations/notices/WhollyOwnedExemptionNotice";
import InHousePharmacyNotice from "@/javascript/manufacturers/shared/designations/notices/InHousePharmacyNotice";
import EmptyCpsNotice from "@/javascript/manufacturers/shared/designations/notices/EmptyCpsNotice";
import NoCpsInRangeNotice from "@/javascript/manufacturers/shared/designations/notices/NoCpsInRangeNotice";

// Fixture data for the Geofence sample map -- not a real covered entity or pharmacy. Shaped to
// match exactly what CeMap.jsx and the "Designated pharmacy" Select expect in production.
const SAMPLE_PHARMACIES = [
  {
    value: "sample-1",
    label: "Sample Pharmacy — Woodward Ave",
    address: "123 Woodward Ave, Detroit, MI",
    lngLat: [-83.0558, 42.3414],
  },
  {
    value: "sample-2",
    label: "Sample Pharmacy — Gratiot Ave",
    address: "456 Gratiot Ave, Detroit, MI",
    lngLat: [-82.9958, 42.3614],
  },
];
const SAMPLE_OUT_OF_RANGE_PHARMACY = {
  value: "sample-3",
  label: "Sample Pharmacy — Out of Range",
  address: "789 Far Rd, Ann Arbor, MI",
  lngLat: [-83.743, 42.2808],
};
const SAMPLE_ALL_PHARMACIES = [...SAMPLE_PHARMACIES, SAMPLE_OUT_OF_RANGE_PHARMACY];

const toPharmacyFeature = (pharmacy) => ({
  type: "Feature",
  geometry: { type: "Point", coordinates: pharmacy.lngLat },
  properties: { id: pharmacy.value, name: pharmacy.label },
});

const SAMPLE_ENTITY = {
  geo_json: [-83.0458, 42.3314],
  pharmacies: {
    details: SAMPLE_ALL_PHARMACIES,
    geo_json: {
      in_range: SAMPLE_PHARMACIES.map(toPharmacyFeature),
      out_of_range: [toPharmacyFeature(SAMPLE_OUT_OF_RANGE_PHARMACY)],
    },
  },
  central_fill: [],
};

// Mirrors PharmacySelect.jsx's own customOption renderer, so the dropdown looks identical to
// the real "Designated pharmacy" select.
const customPharmacyOption = (optionProps) => (
  <div
    {...optionProps.innerProps}
    style={{
      margin: "5px 10px",
      paddingBottom: 5,
      borderBottom: "1px solid #F2F5F8",
      cursor: "pointer",
    }}
  >
    <div style={{ fontSize: 11, fontWeight: 500 }}>{optionProps.data.label}</div>
    <div style={{ color: "#718291", fontSize: 11 }}>{optionProps.data.address || "--"}</div>
  </div>
);

// Renders the real CeMap component (40-mile radius, in-range/out-of-range pharmacy pins) paired
// with the real "Designated pharmacy" Select, against the fixture data above -- kind="retail" and
// excludeCentralFill={false} so CeMap takes its simplest branch (show every in-range pharmacy).
function GeofenceSample() {
  const [selectedPharmacy, setSelectedPharmacy] = useState(null);

  const handleSelectedPharmacy = (entity, id) => {
    const pharmacy = SAMPLE_ALL_PHARMACIES.find((p) => p.value === id);
    setSelectedPharmacy(pharmacy || null);
  };

  return (
    <div>
      <CeMap
        entity={SAMPLE_ENTITY}
        kind="retail"
        manufacturer="geofence-sample"
        selectedPharmacy={selectedPharmacy}
        handleSelectedPharmacy={handleSelectedPharmacy}
        excludeCentralFill={false}
      />
      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 500, marginBottom: 4 }}>Designated pharmacy</div>
        <Select
          isClearable
          placeholder="Search Pharmacies"
          styles={customSelect}
          value={selectedPharmacy}
          options={SAMPLE_ALL_PHARMACIES}
          components={{ Option: customPharmacyOption }}
          onChange={(selection) => setSelectedPharmacy(selection)}
        />
      </div>
    </div>
  );
}

// Example props chosen to force each real notice component into its "visible" state, so these
// previews render the exact production component/markup, not a hand-recreated approximation.
const NOTICE_PREVIEWS = [
  {
    key: "grantee",
    label: "Grantee",
    render: () => (
      <GranteeExemptionNotice disableGranteeNotice={false} isGrantee policy_doc_url="#" />
    ),
  },
  {
    key: "whollyOwned",
    label: "Wholly Owned",
    render: () => (
      <WhollyOwnedExemptionNotice
        disableWhollyOwnedNotice={false}
        hasWoDesignation
        policy_doc_url="#"
      />
    ),
  },
  {
    key: "inHouse",
    label: "In House",
    render: () => (
      <InHousePharmacyNotice disableInHouseNotice={false} hasInHouseCp policy_doc_url="#" />
    ),
  },
  {
    key: "emptyCps",
    label: "Empty CPs",
    render: () => <EmptyCpsNotice disableEmptyCpsNotice={false} emptyCps />,
  },
  {
    key: "noCpsInRange",
    label: "No CPs in Range",
    render: () => (
      <>
        <p className="preview-variant-label">Geofence = Yes (hasMap)</p>
        <NoCpsInRangeNotice disableNoCpsInRangeNotice={false} noCpsInRange hasMap />
        <p className="preview-variant-label">Geofence = No (!hasMap)</p>
        <NoCpsInRangeNotice disableNoCpsInRangeNotice={false} noCpsInRange hasMap={false} />
      </>
    ),
  },
  {
    key: "geofence",
    label: "Geofence",
    render: () => (
      <>
        <p className="preview-explainer">
          Geofence has no notice banner of its own &mdash; it toggles the <code>hasMap</code> prop
          on "No CPs in Range" above (changing "within 40 miles" vs. "within this limited
          distribution network") and determines whether pharmacy selection uses the
          mileage-restricted CeMap picker instead of a plain list. Below is the real CeMap +
          "Designated pharmacy" select, rendered against fixture data (not a real covered entity):
          click a pin or pick from the dropdown to see them stay in sync, just like production.
        </p>
        <GeofenceSample />
      </>
    ),
  },
];

const CONDITIONS = [
  { key: "grantee", disableKey: "disableGranteeNotice", label: "Grantee" },
  { key: "whollyOwned", disableKey: "disableWhollyOwnedNotice", label: "Wholly Owned" },
  { key: "inHouse", disableKey: "disableInHouseNotice", label: "In House" },
  { key: "emptyCps", disableKey: "disableEmptyCpsNotice", label: "Empty CPs" },
  { key: "noCpsInRange", disableKey: "disableNoCpsInRangeNotice", label: "No CPs in Range" },
];

const buildNetworkKeySet = (networks) =>
  new Set(networks.map(({ manufacturer, network }) => `${manufacturer}:${network}`));

// Blocked's `manufacturer` column uses "abbvie" even for Imbruvica-related rows, never
// "pharmacyclics" -- same substitution the Orphan Drug lookup needs.
const blockedManufacturerKey = (manufacturerKey) =>
  manufacturerKey === "pharmacyclics" ? "abbvie" : manufacturerKey;

const buildRows = (
  manufacturerDesignationOptions,
  manufacturerNames,
  orphanDrugKeySet,
  centralFillManufacturerSet,
  ldnKeySet,
  entityBlockKeySet,
  pharmacyBlockKeySet
) => {
  const rows = [];

  Object.keys(manufacturerDesignationOptions).forEach((manufacturerKey) => {
    const manufacturerLabel = manufacturerNames[manufacturerKey] || manufacturerKey;
    const networks = manufacturerDesignationOptions[manufacturerKey];

    Object.keys(networks).forEach((networkKey) => {
      const options = networks[networkKey];
      const key = `${manufacturerKey}:${networkKey}`;
      const orphanDrug = orphanDrugKeySet.has(key);
      const hasLdnRecord = ldnKeySet.has(key);
      // Can't be enforcing a block that isn't even configured in the `blocked` table, for either
      // mechanism -- entity-level (isBlocked/NotPermitted) or pharmacy-level (filterPharmacies).
      const enforcesEntityBlock = entityBlockKeySet.has(key) && !!options.appliesEntityBlockFilter;
      const enforcesPharmacyBlock =
        pharmacyBlockKeySet.has(key) && !!options.appliesPharmacyBlockFilter;

      rows.push({
        manufacturerLabel,
        networkLabel: options.label || networkKey,
        conditions: CONDITIONS.map((condition) => ({
          ...condition,
          visible: !options[condition.disableKey],
        })),
        geofence: !!options.hasMap,
        orphanDrug,
        enforcesOrphanDrug: orphanDrug && !!options.enforcesOrphanDrug,
        blocksCentralFill: !!options.excludeCentralFill,
        // A manufacturer-specific `blocked` row (vs. relying on the default/global rule) means
        // this manufacturer maintains its own custom central-fill block list.
        hasCustomCentralFillList: centralFillManufacturerSet.has(
          blockedManufacturerKey(manufacturerKey)
        ),
        // Can't be enforcing LDN membership if there's no actual Ldn record for this network.
        enforcesLdn: hasLdnRecord && !!options.appliesLdnFilter,
        enforcesBlockList: enforcesEntityBlock || enforcesPharmacyBlock,
        notes: options.notes || "-",
      });
    });
  });

  return rows;
};

const YesNo = ({ visible, detail }) => (
  <span className={visible ? "yes" : "no"}>
    {visible ? (detail ? `Yes - ${detail}` : "Yes") : "No"}
  </span>
);

// Manufacturer and Network stay fixed regardless of whether Notes is shown. Notes (only present
// when shown) is also fixed. Everything else -- Enforces LDN, Enforces Orphan Drug, Enforces Block
// List, Blocks Central Fill, Geofence, and the 5 CONDITIONS columns -- evenly divides what's left.
const MANUFACTURER_WIDTH = 10;
const NETWORK_WIDTH = 10;
const NOTES_WIDTH = 14;
const REMAINING_COLUMN_COUNT = 10;

const buildColumnWidths = (showNotes) => {
  const fixedWidth = MANUFACTURER_WIDTH + NETWORK_WIDTH + (showNotes ? NOTES_WIDTH : 0);
  const remainingWidth = (100 - fixedWidth) / REMAINING_COLUMN_COUNT;
  const middleWidths = Array(REMAINING_COLUMN_COUNT).fill(remainingWidth);

  return showNotes
    ? [MANUFACTURER_WIDTH, NETWORK_WIDTH, ...middleWidths, NOTES_WIDTH]
    : [MANUFACTURER_WIDTH, NETWORK_WIDTH, ...middleWidths];
};

export default function DesignationPreview({
  manufacturerDesignationOptions = {},
  manufacturerNames = {},
  orphanDrugNetworks = [],
  centralFillManufacturers = [],
  ldnNetworks = [],
  entityBlockNetworks = [],
  pharmacyBlockNetworks = [],
  showNotes = false,
}) {
  const columnWidths = buildColumnWidths(showNotes);
  const orphanDrugKeySet = buildNetworkKeySet(orphanDrugNetworks);
  const centralFillManufacturerSet = new Set(centralFillManufacturers);
  const ldnKeySet = buildNetworkKeySet(ldnNetworks);
  const entityBlockKeySet = buildNetworkKeySet(entityBlockNetworks);
  const pharmacyBlockKeySet = buildNetworkKeySet(pharmacyBlockNetworks);
  const rows = buildRows(
    manufacturerDesignationOptions,
    manufacturerNames,
    orphanDrugKeySet,
    centralFillManufacturerSet,
    ldnKeySet,
    entityBlockKeySet,
    pharmacyBlockKeySet
  );
  let previousManufacturer = null;

  return (
    <div className="designation-preview">
      <h1>Designation Notice Preview</h1>
      <p className="subtitle">
        This table is composed from <code>Manufacturers::DesignationConfig::OPTIONS</code> (
        <code>app/models/manufacturers/designation_config.rb</code>), passed in as the{" "}
        <code>manufacturerDesignationOptions</code> prop so this data stays server-side rather than
        shipping in the public JS bundle.
      </p>
      <p className="subtitle">
        "Enforces LDN" is "Yes" only when there's an actual <code>ldns</code> table record for that
        network AND the matching Cypress "LDN network membership" test confirms the app restricts
        the Designated pharmacy select to just those <code>ldns</code>
        -tagged arrangements (its <code>appliesLdnFilter</code> field).
      </p>
      <p className="subtitle">
        "Enforces Orphan Drug" is "Yes" only when there are actual <code>blocked</code> table
        records for ACA Expansion Hospitals (manufacturer+network combos where all 4
        orphan-drug-exempt type codes are blocked), but also considers each manufacturer's{" "}
        <code>cypress/e2e/designations/*_designations.cy.js</code> <code>isOrphanDrug</code> field
        &mdash; whether the app actually checks CAH/CAN/RRC/SCH and blocks designation for them, not
        just whether the data is configured to.
      </p>
      <p className="subtitle">
        "Enforces Block List" is "Yes" if either an entity-level (<code>kind: 'entity'</code>) or
        pharmacy-level (<code>kind: 'pharmacy'</code>) <code>blocked</code> record exists for that
        network AND the matching Cypress "entity block list"/"pharmacy block list" test confirms the
        app enforces it (<code>appliesEntityBlockFilter</code>/
        <code>appliesPharmacyBlockFilter</code>).
      </p>
      <p className="subtitle">
        "Blocks Central Fill" mirrors the manufacturer's own <code>excludeCentralFill</code> code
        flag; when it's "Yes" and that manufacturer also has its own manufacturer-specific row in
        the <code>blocked</code> table (rather than relying on the default/global rule), it reads
        "Yes - Custom List" instead.
      </p>

      <div className="stat-cards__notice stat-cards__notice--mb">
        <strong>Notes:</strong> &nbsp;For the various notices, "Yes" means the notice is enabled and
        will show when its underlying condition is true for a real covered entity. For every
        manufacturer other than AbbVie/Pharmacyclics, the live designation forms do not read from
        this file yet &mdash; their disable props are still hardcoded inline in their own{" "}
        <code>*Form.jsx</code>/<code>PharmacySelect.jsx</code> files, so this page reflects a
        point-in-time audit of that code, not a live computation of it. Several manufacturers define
        a per-state <code>has_state_exemption</code> method in their Ruby policy class, but it is
        not called anywhere else in the codebase &mdash; treat any such mention in Notes as
        informational, not active behavior. Alkermes, Organon, and Gilead have been fully
        deactivated (commented out of the manufacturer enum) and are intentionally excluded from
        this table.
      </div>

      <div className="notice-previews">
        {NOTICE_PREVIEWS.map((notice) => (
          <details key={notice.key}>
            <summary>{notice.label} &mdash; what it looks like</summary>
            <div className="notice-preview-body">{notice.render()}</div>
          </details>
        ))}
      </div>

      <table>
        <colgroup>
          {columnWidths.map((width, index) => (
            <col key={index} style={{ width: `${width}%` }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th>Manufacturer</th>
            <th>Network</th>
            <th>Enforces LDN</th>
            <th>Enforces Orphan Drug</th>
            <th>Enforces Block List</th>
            <th>Blocks Central Fill</th>
            <th>Geofence</th>
            {CONDITIONS.map((condition) => (
              <th key={condition.key}>{condition.label}</th>
            ))}
            {showNotes && <th>Notes</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const isNewManufacturer = row.manufacturerLabel !== previousManufacturer;
            previousManufacturer = row.manufacturerLabel;

            return (
              <tr key={index} className={isNewManufacturer ? "new-manufacturer" : ""}>
                <td className="manufacturer">{row.manufacturerLabel}</td>
                <td>{row.networkLabel}</td>
                <td>
                  <YesNo visible={row.enforcesLdn} />
                </td>
                <td>
                  <YesNo visible={row.enforcesOrphanDrug} />
                </td>
                <td>
                  <YesNo visible={row.enforcesBlockList} />
                </td>
                <td>
                  <YesNo
                    visible={row.blocksCentralFill}
                    detail={row.hasCustomCentralFillList ? "Custom List" : null}
                  />
                </td>
                <td>
                  <YesNo visible={row.geofence} />
                </td>
                {row.conditions.map((condition) => (
                  <td key={condition.key}>
                    <YesNo visible={condition.visible} />
                  </td>
                ))}
                {showNotes && <td className="notes">{row.notes}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
